/**
 * VerifySignaturePage — Page publique d'authenticité (Iter385)
 *
 * Route : /verify/:bookingId?sig=...
 * Aucune authentification requise. Aucune donnée sensible affichée.
 * Design ultra minimaliste, audit-ready.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ShieldCheck, Clock } from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

function StatusBlock({ ok, children, accent }) {
  const bg = ok ? '#FBF9F3' : '#FFF5F5';
  const border = ok ? '#E8DDB5' : '#FCA5A5';
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, padding: 28 }}>
      {children}
    </div>
  );
}

export default function VerifySignaturePage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const sig = params.get('sig') || '';
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!bookingId || !sig) {
        setState({ status: 'invalid', data: null, error: 'Lien invalide' });
        return;
      }
      try {
        const res = await fetch(`${API}/public/verify-signature?bid=${encodeURIComponent(bookingId)}&sig=${encodeURIComponent(sig)}`);
        if (cancelled) return;
        if (res.status === 429) {
          setState({ status: 'rate_limited', data: null, error: 'Trop de vérifications consécutives. Réessaye dans une minute.' });
          return;
        }
        if (res.status === 503) {
          setState({ status: 'unavailable', data: null, error: 'Service de vérification temporairement indisponible.' });
          return;
        }
        if (!res.ok) {
          setState({ status: 'not_found', data: null, error: 'Document non vérifiable' });
          return;
        }
        const data = await res.json();
        setState({ status: 'ok', data, error: null });
      } catch (err) {
        if (!cancelled) setState({ status: 'network', data: null, error: 'Erreur réseau' });
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, sig]);

  return (
    <div
      style={{
        minHeight: '100vh', background: '#0F0E0C',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', fontFamily: 'Georgia, serif',
      }}
      data-testid="verify-signature-page"
    >
      <div style={{ width: '100%', maxWidth: 460 }}>
        {/* Logo header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#D4AF37', letterSpacing: '0.04em' }}>
              COACHDIA
            </div>
            <div style={{ fontSize: 10, color: '#6B5E48', letterSpacing: '0.24em', textTransform: 'uppercase', marginTop: 2 }}>
              Vérification d'authenticité
            </div>
          </Link>
        </div>

        {state.status === 'loading' && (
          <div style={{ background: '#FBF9F3', borderRadius: 16, padding: 40, textAlign: 'center' }}>
            <Loader2 className="animate-spin" style={{ color: '#92710A', width: 28, height: 28, margin: '0 auto' }} />
            <p style={{ marginTop: 16, color: '#6B5E48', fontFamily: 'system-ui', fontSize: 14 }}>Vérification en cours…</p>
          </div>
        )}

        {state.status === 'ok' && state.data && (
          <StatusBlock ok>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <CheckCircle style={{ color: '#15803D', width: 48, height: 48, margin: '0 auto' }} />
              <h1 data-testid="verify-status-ok" style={{ fontSize: 22, color: '#15803D', margin: '12px 0 4px', fontWeight: 400 }}>
                Rapport authentique
              </h1>
              <p style={{ fontSize: 12, color: '#6B5E48', fontFamily: 'system-ui' }}>
                Vérification effectuée par COACHDIA
              </p>
            </div>

            <dl style={{ fontFamily: 'system-ui', fontSize: 14, color: '#1F1A12', lineHeight: 1.8 }}>
              <Row label="Coach" value={state.data.coach_name} />
              <Row label="Date session" value={state.data.date} />
              <Row label="Durée attestée" value={`${state.data.duration_min} min`} />
              <Row label="Finalisé le" value={formatDateTime(state.data.finalized_at)} />
              <Row label="Signature certifiée" value={state.data.signature_short} mono />
            </dl>

            <div style={{
              marginTop: 24, paddingTop: 16, borderTop: '1px dashed #E8DDB5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              color: '#92710A', fontSize: 12, fontFamily: 'system-ui',
            }}>
              <ShieldCheck style={{ width: 14, height: 14 }} />
              <span>Empreinte SHA-256 validée · Émetteur {state.data.issuer}</span>
            </div>
          </StatusBlock>
        )}

        {(state.status === 'not_found' || state.status === 'invalid') && (
          <StatusBlock>
            <div style={{ textAlign: 'center' }}>
              <XCircle style={{ color: '#DC2626', width: 48, height: 48, margin: '0 auto' }} />
              <h1 data-testid="verify-status-fail" style={{ fontSize: 20, color: '#DC2626', margin: '12px 0 4px', fontWeight: 400 }}>
                Authenticité non vérifiée
              </h1>
              <p style={{ fontSize: 13, color: '#7C2D12', fontFamily: 'system-ui', maxWidth: 360, margin: '12px auto 0', lineHeight: 1.5 }}>
                Ce document ne provient pas de COACHDIA ou a été modifié.
              </p>
            </div>
          </StatusBlock>
        )}

        {state.status === 'rate_limited' && (
          <StatusBlock>
            <div style={{ textAlign: 'center' }}>
              <Clock style={{ color: '#D97706', width: 40, height: 40, margin: '0 auto' }} />
              <h1 style={{ fontSize: 18, color: '#92400E', margin: '12px 0 4px', fontWeight: 400 }}>Trop de tentatives</h1>
              <p style={{ fontSize: 13, color: '#7C2D12', fontFamily: 'system-ui' }}>{state.error}</p>
            </div>
          </StatusBlock>
        )}

        {(state.status === 'network' || state.status === 'unavailable') && (
          <StatusBlock>
            <div style={{ textAlign: 'center', color: '#6B5E48', fontFamily: 'system-ui', fontSize: 14 }}>{state.error}</div>
          </StatusBlock>
        )}

        <p style={{ textAlign: 'center', marginTop: 22, fontSize: 11, color: '#6B5E48', fontFamily: 'system-ui' }}>
          Plateforme de coaching certifié — Service public de vérification
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
      <dt style={{ color: '#6B5E48', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontFamily: mono ? 'Menlo, monospace' : undefined, fontSize: mono ? 12 : 14, color: '#1F1A12' }}>
        {value || '—'}
      </dd>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) + ' UTC';
  } catch {
    return iso;
  }
}
